import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { Select } from "@/components/Select";
import { PrioritySelect } from "@/components/PrioritySelect";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { tasksApi, authApi, developmentApi, taskTypesApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

interface NewTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (task: any) => void;
    statuses?: any[];
    types?: any[];
    task?: any | null;
    prefilledStatusId?: number | null;
    prefilledSprintId?: number | null;
    prefilledDevelopmentId?: number | string | null;
    prefilledRelatedTaskIds?: number[];
    prefilledDependencyIds?: number[];
}

export const NewTaskModal: React.FC<NewTaskModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    statuses = [],
    types = [],
    task,
    prefilledStatusId,
    prefilledSprintId,
    prefilledDevelopmentId,
    prefilledRelatedTaskIds = [],
    prefilledDependencyIds = [],
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const { user } = useAuth();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [statusId, setStatusId] = useState("");
    const [availableStatuses, setAvailableStatuses] = useState<any[]>(statuses);
    const [availableTypes, setAvailableTypes] = useState<any[]>(types);
    const [typeId, setTypeId] = useState("");
    const [priority, setPriority] = useState<number>(2);
    const [assignees, setAssignees] = useState<number[]>([]);
    const [developmentId, setDevelopmentId] = useState<string>("");
    const [relatedTasks, setRelatedTasks] = useState<number[]>([]);
    const [dependencies, setDependencies] = useState<number[]>([]);

    const [users, setUsers] = useState<any[]>([]);
    const [developments, setDevelopments] = useState<any[]>([]);
    const [allTasks, setAllTasks] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            taskTypesApi.list().then((response) => {
                const nextTypes = Array.isArray(response.data) ? response.data : response.data?.types;
                if (response.result && Array.isArray(nextTypes)) setAvailableTypes(nextTypes);
            }).catch(() => setAvailableTypes(types));

            if (task) {
                setTitle(task.title || "");
                setDescription(task.description || "");
                setStatusId(task.status_id ? String(task.status_id) : "");
                setTypeId(task.type_id ? String(task.type_id) : task.type?.id ? String(task.type.id) : "");
                setPriority(
                    typeof task.priority === "number"
                        ? task.priority
                        : parseInt(task.priority, 10) || 2
                );
                setAssignees(
                    task.assignee_id
                        ? [task.assignee_id]
                        : task.assignee?.id
                          ? [task.assignee.id]
                          : []
                );
                setDevelopmentId(
                    task.development_id ? String(task.development_id) : ""
                );
                setRelatedTasks(
                    task.related_tasks?.map((t: any) => t.id) || []
                );
                setDependencies(task.dependencies?.map((t: any) => t.id) || []);
            } else {
                setTitle("");
                setDescription("");
                const defaultType = types.find((item) => item.is_default) || types[0];
                setTypeId(defaultType ? String(defaultType.id) : "");
                setPriority(2);
                if (prefilledStatusId) {
                    setStatusId(String(prefilledStatusId));
                } else {
                    const todoStatus =
                        statuses.find((s) => s.is_default) || statuses[0];
                    setStatusId(todoStatus ? String(todoStatus.id) : "");
                }
                if (user) {
                    setAssignees([user.id]);
                } else {
                    setAssignees([]);
                }
                if (prefilledDevelopmentId) {
                    setDevelopmentId(String(prefilledDevelopmentId));
                } else {
                    setDevelopmentId("");
                }
                setRelatedTasks(prefilledRelatedTaskIds);
                setDependencies(prefilledDependencyIds);
            }

            authApi
                .users()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setUsers(res.data);
                    }
                })
                .catch((err) =>
                    console.error("Error loading users for assignees", err)
                );

            developmentApi
                .list()
                .then((res) => {
                    const items = Array.isArray(res.data)
                        ? res.data
                        : res.data?.items;
                    if (res.result && Array.isArray(items)) {
                        setDevelopments(items);
                    }
                })
                .catch((err) =>
                    console.error("Error loading developments", err)
                );

            tasksApi
                .list()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setAllTasks(res.data);
                    }
                })
                .catch((err) => console.error("Error loading tasks", err));
        }
    }, [isOpen, task, statuses, types, prefilledStatusId, prefilledRelatedTaskIds, prefilledDependencyIds, user]);

    useEffect(() => {
        if (Array.isArray(statuses)) {
            setAvailableStatuses(statuses);
        }
    }, [statuses]);

    useEffect(() => {
        if (isOpen && !task && availableTypes.length > 0 && !typeId) {
            const defaultType = availableTypes.find((item) => item.is_default) || availableTypes[0];
            setTypeId(String(defaultType.id));
        }
    }, [availableTypes, isOpen, task, typeId]);

    useEffect(() => {
        if (isOpen && !task && !prefilledStatusId && availableStatuses.length > 0) {
            const defaultStatus = availableStatuses.find((status) => status.is_default) || availableStatuses[0];
            setStatusId(String(defaultStatus.id));
        }
    }, [availableStatuses, isOpen, prefilledStatusId, task]);

    const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
        e?.preventDefault();

        if (!title.trim() || !statusId) {
            showError(t("tasks.new_modal.error_mandatory"));
            return;
        }

        setLoading(true);
        try {
            if (task && task.id) {
                const res = await tasksApi.update(task.id, {
                    title,
                    description,
                    status_id: parseInt(statusId),
                    type_id: typeId ? parseInt(typeId) : null,
                    priority,
                    assignee_id: assignees.length > 0 ? assignees[0] : null,
                    development_id: developmentId
                        ? parseInt(developmentId)
                        : null,
                    related_tasks: relatedTasks,
                    dependencies: dependencies,
                });

                if (res.result) {
                    showSuccess(t("tasks.updated"));
                    onSuccess(res.data);
                    onClose();
                } else {
                    showError(res.message || t("errors.default"));
                }
            } else {
                const res = await tasksApi.create({
                    title,
                    description,
                    status_id: parseInt(statusId),
                    type_id: typeId ? parseInt(typeId) : null,
                    priority,
                    sprint_id: prefilledSprintId || null,
                    assignee_id: assignees.length > 0 ? assignees[0] : null,
                    development_id: developmentId
                        ? parseInt(developmentId)
                        : null,
                    related_tasks: relatedTasks,
                    dependencies: dependencies,
                });

                if (res.result) {
                    showSuccess(t("tasks.new_modal.success"));
                    onSuccess(res.data);
                    onClose();
                } else {
                    showError(res.message || t("tasks.new_modal.error_create"));
                }
            }
        } catch (err) {
            console.error("Error al guardar tarea:", err);
            showError(t("tasks.new_modal.error_connection"));
        } finally {
            setLoading(false);
        }
    };

    const resolvedStatuses = availableStatuses.length > 0 ? availableStatuses : statuses;
    const statusOptions = resolvedStatuses.map((s) => ({
        value: String(s.id),
        label: t(`tasks.statuses.${s.tag || "todo"}`, { defaultValue: s.name || t("tasks.statuses.todo") }),
    }));
    const typeOptions = availableTypes.map((type) => {
        const typeTag = type.tag === "development" ? "task" : type.tag;
        const key = `tasks.types.${typeTag}`;
        const translatedLabel = t(key);
        return {
            value: String(type.id),
            label: translatedLabel !== key ? translatedLabel : type.name,
            icon: type.icon || (typeTag === "bug" ? "bug" : "clipboardCheck"),
            iconColor: typeTag === "bug" ? "text-rose-500" : "text-primary",
        };
    });

    const assigneeOptions = useMemo(() => {
        return users
            .filter(
                (u) =>
                    u.roles?.includes("it") || u.roles?.includes("development")
            )
            .map((u) => ({
                id: u.id,
                name: u.name,
                subtitle: u.email,
            }));
    }, [users]);

    const developmentOptions = useMemo(() => {
        return developments.map((d) => ({
            value: String(d.id),
            label: `${d.code} - ${d.title}`,
        }));
    }, [developments]);

    const taskOptions = useMemo(() => {
        return allTasks
            .filter((t) => !task || t.id !== task.id)
            .map((t) => ({
                id: t.id,
                name: `${t.code} - ${t.title}`,
            }));
    }, [allTasks, task]);

    const modalTitle = task
        ? `${task.code ? `${task.code} - ` : ""}${t("tasks.edit_title")}`
        : t("tasks.new_modal.title");

    const submitLabel = task ? t("common.save") : t("tasks.new_modal.submit");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            cancelLabel={t("tasks.new_modal.cancel")}
            isSubmitting={loading}
            submitDisabled={!title.trim() || !statusId}
            maxWidth="xl"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                        {t("tasks.new_modal.type")}
                    </label>
                    <Select options={typeOptions} value={typeId} onChange={setTypeId} full />
                </div>

                <Input
                    label={t("tasks.new_modal.title_label")}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("tasks.new_modal.title_placeholder")}
                    required
                />

                <Textarea
                    label={t("tasks.new_modal.description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("tasks.new_modal.description_placeholder")}
                    rows={8}
                />

                <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                        {t("tasks.new_modal.status")}
                    </label>
                    <Select
                        options={statusOptions}
                        value={statusId}
                        onChange={setStatusId}
                        full
                    />
                </div>

                <div>
                    <PrioritySelect
                        value={priority}
                        onChange={setPriority}
                        label={t("tasks.new_modal.priority")}
                        full
                    />
                </div>

                <SearchableMultiSelect
                    label={t("tasks.new_modal.assignees_label")}
                    value={assignees}
                    onChange={setAssignees}
                    options={assigneeOptions}
                    placeholder={t("tasks.new_modal.assignees_placeholder")}
                    singleSelect={true}
                />

                <SearchableMultiSelect
                    label={t("tasks.new_modal.development")}
                    value={developmentId ? [parseInt(developmentId)] : []}
                    onChange={(val: number[]) =>
                        setDevelopmentId(val.length > 0 ? String(val[0]) : "")
                    }
                    options={developmentOptions.map((d) => ({
                        id: parseInt(d.value),
                        name: d.label,
                    }))}
                    placeholder={
                        t("tasks.new_modal.development_placeholder") ||
                        "Seleccionar desarrollo"
                    }
                    singleSelect={true}
                />

                <SearchableMultiSelect
                    label={
                        t("tasks.new_modal.related_tasks") ||
                        "Tareas relacionadas"
                    }
                    value={relatedTasks}
                    onChange={setRelatedTasks}
                    options={taskOptions}
                    placeholder={
                        t("tasks.new_modal.related_tasks_placeholder") ||
                        "Buscar tareas"
                    }
                />

                <SearchableMultiSelect
                    label={t("tasks.new_modal.dependencies") || "Dependencias"}
                    value={dependencies}
                    onChange={setDependencies}
                    options={taskOptions}
                    placeholder={
                        t("tasks.new_modal.dependencies_placeholder") ||
                        "Buscar tareas bloqueantes"
                    }
                />
            </div>
        </Modal>
    );
};
